import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  GetUsersParams,
  GetUsersResponse,
} from '../interfaces/get-users';
import { RequestError } from '../../../interfaces/error.interface';
import { GetUserResponse } from '../interfaces/get-user';
import { User } from '../../../interfaces/user.interface';
import { StudentCourse } from '../../../interfaces/studentCourse.interface';
import { GetCoursesResponse } from '../interfaces/get-courses';
import { Course } from '../../../interfaces/course.interface';
import { GetSubscriptionsResponse } from '../interfaces/get-subscriptions';
import { Subscription } from '../../../interfaces/subscription.interface';
import { Rating } from '../../../interfaces/rating.interface';
import { GetRatingsResponse } from '../interfaces/get-ratings';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';

export interface UserState {
  users: {
    ids: string[];
    entities: Record<string, User>;
  };
  join: boolean;
  user1: User | null;
  user2: User | null;
  user: User | null;
  options: GetUsersParams;
  subscriptions: Subscription[];
  ratings: Rating[];
  professorCourses: Course[];
  page: number;
  total: number;
  noMore: boolean;
  studentCourses: StudentCourse[];
  errorCode: number | null;
}

const initialState: UserState = {
  users: {
    ids: [],
    entities: {},
  },
  user: null,
  user1: null,
  user2: null,
  join: false,
  options: {
    active: true
  },
  page: 1,
  total: 0,
  noMore: false,
  subscriptions: [],
  ratings: [],
  professorCourses: [],
  studentCourses: [],
  errorCode: null,
};

const userAdapter = createEntityAdapter<User>((user: User) => user.id);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUsersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getUsersSuccessApiAction: (state, action: PayloadAction<GetUsersResponse>) => {
      userAdapter.updateMany(action.payload.users, state.users);
      state.total = action.payload.total;
      state.noMore = action.payload.users.length < 100;
    },
    getUsersFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getUserInitApiAction: (state) => {
      state.errorCode = null;
      state.user = null;
    },
    getUserSuccessApiAction: (state, action: PayloadAction<GetUserResponse>) => {
      state.user = action.payload.user;
    },
    getUserFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postUserInitApiAction: (state) => {
      state.errorCode = null;
    },
    postUserSuccessApiAction: () => {},
    postUserFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putUserInitApiAction: (state) => {
      state.errorCode = null;
    },
    putUserSuccessApiAction: () => {},
    putUserFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putBlockUserInitApiAction: () => {},
    putBlockUserSuccessApiAction: () => {},
    putBlockUserFailuireApiAction: () => {},
    postJoinUserInitApiAction: () => {},
    postJoinUserSuccessApiAction: () => {},
    postJoinUserFailuireApiAction: () => {},
    putUnblockUserInitApiAction: () => {},
    putUnblockUserSuccessApiAction: () => {},
    putUnblockUserFailuireApiAction: () => {},
    getCoursesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCoursesSuccessApiAction: (state, action: PayloadAction<GetCoursesResponse>) => {
      state.professorCourses = action.payload.professorCourses;
      state.studentCourses = action.payload.studentCourses;
    },
    getCoursesFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getSubscriptionsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getSubscriptionsSuccessApiAction: (state, action: PayloadAction<GetSubscriptionsResponse>) => {
      state.subscriptions = action.payload.subscriptions;
    },
    getSubscriptionsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getRatingsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getRatingssSuccessApiAction: (state, action: PayloadAction<GetRatingsResponse>) => {
      state.ratings = action.payload.ratings;
    },
    getRatingsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    setOptionsAction: (state, action: PayloadAction<GetUsersParams>) => {
      state.options = action.payload;
      userAdapter.removeAll(state.users);
      state.page = 1;
    },
    setJoinAction: (state, action: PayloadAction<boolean>) => {
      state.join = action.payload;

      if (!state.join) {
        state.user1 = null;
        state.user2 = null;
      }
    },
    setUser1Action: (state, action: PayloadAction<User | null>) => {
      state.user1 = action.payload;
    },
    setUser2Action: (state, action: PayloadAction<User | null>) => {
      state.user2 = action.payload;
    },
    resetOptionsAction: (state) => {
      state.options = {
        active: true
      };
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    userClearAction: (state) => {
      userAdapter.removeAll(state.users);
      state.user = null;
      state.user1 = null;
      state.user2 = null;
      state.join = false;
      state.page = 1;
      state.total = 0;
      state.noMore = false;
      state.professorCourses = [];
      state.studentCourses = [];
      state.subscriptions = [];
      state.ratings = [];
      state.errorCode = null;
    },
  },
});

export const {
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  getUsersFailureApiAction,
  getUserInitApiAction,
  getUserSuccessApiAction,
  getUserFailureApiAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  postUserFailuireApiAction,
  putUserInitApiAction,
  putUserSuccessApiAction,
  putUserFailuireApiAction,
  putBlockUserInitApiAction,
  putBlockUserSuccessApiAction,
  putBlockUserFailuireApiAction,
  putUnblockUserInitApiAction,
  putUnblockUserSuccessApiAction,
  putUnblockUserFailuireApiAction,
  getCoursesInitApiAction,
  getCoursesSuccessApiAction,
  getCoursesFailureApiAction,
  getSubscriptionsInitApiAction,
  getSubscriptionsSuccessApiAction,
  getSubscriptionsFailureApiAction,
  getRatingsInitApiAction,
  getRatingssSuccessApiAction,
  getRatingsFailureApiAction,
  postJoinUserInitApiAction,
  postJoinUserSuccessApiAction,
  postJoinUserFailuireApiAction,
  setJoinAction,
  setUser1Action,
  setUser2Action,
  increasePageAction,
  resetPageAction,
  setOptionsAction,
  resetOptionsAction,
  userClearAction,
} = userSlice.actions;

export default userSlice.reducer;
